import axios from 'axios';
import apiConfig from '../components/config/apiConfig';
import authService from './authService';
import { showSessionExpiredNotification } from '../utils/notification';

const API_BASE_URL = apiConfig.BASE_URL;

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  async (config) => {
    // Determine which token to use based on the URL path
    let token = null;
    
    if (config.url.includes('/admin/')) {
      // Check token validity for admin routes (except login)
      if (!config.url.includes('/admin/login')) {
        // Check if admin token is valid
        if (!authService.isAdminLoggedIn()) {
          // Show notification and redirect
          await showSessionExpiredNotification();
          authService.adminLogout();
          window.location.href = '/admin/login';
          return Promise.reject(new Error('Admin session expired'));
        }
      }
      token = authService.getAdminToken();
    } else if (!config.url.includes('/public/')) {
      // For non-public routes that are not admin routes, check member token
      if (!config.url.includes('/member/login') && !config.url.includes('/member/register')) {
        // Check if member token is valid
        if (!authService.isMemberLoggedIn() && authService.getMemberToken()) {
          // Token exists but is expired
          await showSessionExpiredNotification();
          authService.memberLogout();
          window.location.href = '/member-login';
          return Promise.reject(new Error('Member session expired'));
        }
      }
      token = authService.getMemberToken();
    }
    
    // Add Authorization header if token exists
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      const errorData = error.response.data || {};
      const isTokenExpired = errorData.code === 'TOKEN_EXPIRED' || 
                            errorData.error === 'Invalid or expired token' ||
                            errorData.message?.includes('expired');
      
      // Show user-friendly notification for expired tokens
      if (isTokenExpired) {
        await showSessionExpiredNotification();
      }
      
      // Check which path we're on to determine which auth to clear
      if (window.location.pathname.startsWith('/admin')) {
        authService.adminLogout();
        window.location.href = '/admin/login';
      } else if (!window.location.pathname.startsWith('/login') && 
                !window.location.pathname.startsWith('/register') &&
                !window.location.pathname.startsWith('/member-login')) {
        authService.memberLogout();
        window.location.href = '/member-login';
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
